/**
 * MER-291: Demo mode + PII obfuscation.
 *
 * Demo mode is a RENDER-LAYER flag. It does not mutate the database
 * and does not bypass RLS; it only changes what the React layer
 * presents on top of the same Supabase queries. Toggled via the
 * `?demo=1` URL query parameter so AJ can paste a single URL to a
 * prospect without provisioning a separate tenant.
 *
 * When demo mode is on:
 *   - DEMO watermark renders in the dashboard banner (DemoBanner.tsx).
 *   - Customer / account names obfuscate to "Customer ABC" (3-letter
 *     base-26 hash of the underlying id, deterministic across reloads
 *     and across widgets so the same row labels stay stable).
 *   - Driver ids obfuscate to "Driver D-12".
 *   - Route ids obfuscate to "Route R-12".
 *   - Division names, equipment types, LOBs, dates, and dollar values
 *     pass through unchanged (already generic).
 *
 * Cowork PII verification (per MER-291 hard rule) happens via Chrome
 * MCP before AJ shares the URL. Any column that surfaces here without
 * obfuscation is a defect; add the column name to PII_COLUMN_HANDLERS
 * and the renderer picks it up automatically.
 */

import { useSearchParams } from "react-router-dom";

export function useDemoMode(): boolean {
  const [params] = useSearchParams();
  const v = params.get("demo");
  return v === "1" || v === "true";
}

/**
 * djb2: small, fast, deterministic, no dependency. Good enough for
 * label assignment. Negative results are folded into positive via abs.
 */
function djb2(str: string): number {
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) + h + str.charCodeAt(i);
    h |= 0; // force 32-bit
  }
  return Math.abs(h);
}

/**
 * Excel-style base-26 alphabet. 0 -> A, 25 -> Z, 26 -> AA, ..., 701 -> ZZ.
 * Width-3 capacity is 26^3 = 17,576 distinct labels which covers DWS's
 * ~8,800 customers with low expected collision.
 */
function toAlpha(n: number, minWidth: number = 1): string {
  if (n < 0) n = -n;
  let s = "";
  let v = n;
  while (true) {
    s = String.fromCharCode(65 + (v % 26)) + s;
    v = Math.floor(v / 26);
    if (v === 0) break;
    v -= 1;
  }
  while (s.length < minWidth) s = "A" + s;
  return s;
}

/**
 * Columns whose values are PII or PII-adjacent and must obfuscate when
 * demo mode is on. Add new entries here as more widgets surface them.
 * Each handler receives the raw value (string or number) and returns
 * the obfuscated display string.
 */
const PII_COLUMN_HANDLERS: Record<string, (raw: unknown) => string> = {
  customer_id: (raw) => `Customer ${toAlpha(djb2(String(raw)) % 17576, 3)}`,
  customer_name: (raw) => `Customer ${toAlpha(djb2(String(raw)) % 17576, 3)}`,
  account_name: (raw) => `Customer ${toAlpha(djb2(String(raw)) % 17576, 3)}`,
  account_number: (raw) => `Customer ${toAlpha(djb2(String(raw)) % 17576, 3)}`,
  driver_id: (raw) => `Driver D-${djb2(String(raw)) % 1000}`,
  driver_name: (raw) => `Driver D-${djb2(String(raw)) % 1000}`,
  route_id: (raw) => `Route R-${djb2(String(raw)) % 1000}`,
};

export function isPIIColumn(columnName: string | undefined | null): boolean {
  if (!columnName) return false;
  return columnName in PII_COLUMN_HANDLERS;
}

/**
 * Apply PII obfuscation if (a) demo mode is on, (b) the column is
 * known PII, and (c) the value is not nullish. Returns the obfuscated
 * label as a string. Otherwise returns the raw value unchanged.
 *
 * Call sites: tabular cell renderer, chart x-axis label formatter,
 * pivot row label formatter. Keep the surface small; if obfuscation
 * needs to spread to more components, factor a single applyPII helper
 * at the data-row level instead.
 */
export function obfuscatePII(
  value: unknown,
  columnName: string | undefined | null,
  demoMode: boolean,
): unknown {
  if (!demoMode) return value;
  if (value === null || value === undefined || value === "") return value;
  if (!columnName) return value;
  const handler = PII_COLUMN_HANDLERS[columnName];
  if (!handler) return value;
  return handler(value);
}
